import React, { Fragment } from 'react'
import { css } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { theme as deprecatedTheme } from '../../../src/styled'
import {
  CollapsibleListManager,
  TransitionUnorderedList,
  TransitionListItem,
  CollapsibleListToggle
} from '../../legacy-design-system/list'
import { FormattedMessage } from 'react-intl'
import { CmdInjectionFindings } from './attack-event-rules/cmd-injection-semantic-analysis-overview'
import { SqlInjectionFindings } from './attack-event-rules/sql-injection-semantic-analysis-overview'
import { PathTraversalFindings } from './attack-event-rules/path-traversal-semantic-analysis-overview'
import {
  AttackEventDetailsSQLInjection,
  AttackEventDetailsSQLInjectionTautologies,
  AttackEventDetailsSQLInjectionChainedQueries,
  AttackEventDetailsSQLInjectionDangerousFunctions,
  AttackEventDetailsSQLInjectionSuspiciousUnions
} from './attack-event-details-sqli/attack-event-details-sqli'
import {
  AttackEventDetailsCommandInjection,
  AttackEventDetailsCommandInjectionChainedCommands,
  AttackEventDetailsCommandInjectionCommandBackdoors,
  AttackEventDetailsCommandInjectionDangerousPaths,
  AttackEventDetailsCommandInjectionProcessHardening
} from './attack-event-details-cmdi/attack-event-details-command-injection'
import {
  AttackEventDetailsPathTraversal,
  AttackEventDetailsPathTraversalFileSecurityBypass
} from './attack-event-details-path-traversal/attack-event-details-path-traversal'
import {
  Application,
  AttackEvent,
  AttackEventDetails,
  RuleName,
  StackFrame,
  UserInput
} from '../attack-event/shared'
import { InputTracingAttackEventDetails } from '../attack-events-grid/drilldown-panel/details/input-tracing-attack-event-details'

export type PossibleFindings = Record<
  CmdInjectionFindings & SqlInjectionFindings & PathTraversalFindings,
  () => JSX.Element
>

interface SemanticRuleDetailsProps {
  readonly application: Application
  readonly userInput?: UserInput
  readonly details?: AttackEventDetails
  readonly file?: string
  readonly method?: string
  readonly line?: number
  readonly stackFrames?: StackFrame[]
  readonly lastCustomFrame?: number
  readonly callLocation?: string
}

const semanticRuleDetailConfiguration: {
  readonly [key: string]: React.FC<SemanticRuleDetailsProps>
} = {
  [RuleName.SQL_INJECTION]: AttackEventDetailsSQLInjection,
  [RuleName.CMD_INJECTION]: AttackEventDetailsCommandInjection,
  [RuleName.PATH_TRAVERSAL]: AttackEventDetailsPathTraversal,
  [RuleName.PATH_TRAVERSAL_FILE_SECURITY_BYPASS]:
    AttackEventDetailsPathTraversalFileSecurityBypass,
  [RuleName.SQL_INJECTION_CHAINED_QUERIES]:
    AttackEventDetailsSQLInjectionChainedQueries,
  [RuleName.SQL_INJECTION_TAUTOLOGIES]:
    AttackEventDetailsSQLInjectionTautologies,
  [RuleName.SQL_INJECTION_SUSPICIOUS_UNIONS]:
    AttackEventDetailsSQLInjectionSuspiciousUnions,
  [RuleName.SQL_INJECTION_DANGEROUS_FUNCTIONS]:
    AttackEventDetailsSQLInjectionDangerousFunctions,
  [RuleName.CMD_INJECTION_CHAINED_COMMANDS]:
    AttackEventDetailsCommandInjectionChainedCommands,
  [RuleName.CMD_INJECTION_BACKDOORS]:
    AttackEventDetailsCommandInjectionCommandBackdoors,
  [RuleName.CMD_INJECTION_DANGEROUS_PATHS]:
    AttackEventDetailsCommandInjectionDangerousPaths,
  [RuleName.CMD_INJECTION_PROCESS_HARDENING]:
    AttackEventDetailsCommandInjectionProcessHardening
}

const getDetailsComponentByRuleName = (ruleUuid: string) => {
  if (!semanticRuleDetailConfiguration[ruleUuid]) {
    // This indicates that we have not yet implemented a details view for a semantic rule with this uuid/name
    throw new Error(
      `Could not find a details component for rule uuid [${ruleUuid}]`
    )
  }
  const ruleName = ruleUuid as RuleName
  return semanticRuleDetailConfiguration[ruleName]
}

export function AttackEventDetail({
  organizationId,
  attackEvent,
  className
}: {
  readonly organizationId: string
  readonly attackEvent: AttackEvent
  readonly className?: string
}) {
  const {
    id,
    ruleUuid,
    details,
    application,
    userInput,
    file,
    line,
    method,
    stackFrames,
    detectionType,
    lastCustomFrame,
    signature,
    vector,
    callLocation
  } = attackEvent
  if (detectionType === 'SEMANTIC_ANALYSIS') {
    const SemanticRuleDetails = getDetailsComponentByRuleName(ruleUuid)

    return (
      <div className={className}>
        {details && (
          <SemanticRuleDetails
            application={application}
            userInput={userInput}
            details={details}
            file={file}
            method={method}
            line={line}
            stackFrames={stackFrames}
            lastCustomFrame={lastCustomFrame}
            callLocation={callLocation}
          />
        )}
      </div>
    )
  }

  return (
    <div className={className}>
      <InputTracingAttackEventDetails
        organizationId={organizationId}
        eventUuid={id}
        application={application}
        userInput={userInput}
        file={file}
        method={method}
        line={line}
        stackFrames={stackFrames}
        lastCustomFrame={lastCustomFrame}
        signature={signature}
        vector={vector}
      />
    </div>
  )
}

interface AttackEventCodeLocationProps {
  readonly file?: string
  readonly method?: string
  readonly line?: number
  readonly stackFrames?: StackFrame[]
  readonly lastCustomFrame?: number
}

interface AttackEventSuspiciousQueryElementsProps {
  readonly labelId: string
  readonly findings: string[]
  readonly possibleFindings: PossibleFindings
  readonly language: string
}

const flexRow = css`
  display: flex;
  flex-direction: row;
`

const paddingLeft = css`
  padding-left: 5em;
`

export function AttackEventCodeLocation({
  file,
  method,
  line,
  stackFrames,
  lastCustomFrame
}: AttackEventCodeLocationProps) {
  return (
    <Fragment>
      <div
        className={css`
          font-weight: ${deprecatedTheme.fontWeights.primary.bold};
        `}
      >
        <FormattedMessage id="title_attackEventDetail_codeLocation" />
      </div>
      <div>
        {file && (
          <div className={flexRow}>
            <div
              className={css`
                flex-basis: 5em;
              `}
            >
              <FormattedMessage
                id="title_attackEventDetail_file"
                children={(text) => <Fragment>{text}:</Fragment>}
              />
            </div>
            <div className={paddingLeft}>{file}</div>
          </div>
        )}
        {method && (
          <div className={flexRow}>
            <div
              className={css`
                flex-basis: 5em;
              `}
            >
              <FormattedMessage
                id="title_method"
                children={(text) => <Fragment>{text}:</Fragment>}
              />
            </div>
            <div className={paddingLeft}>{method}</div>
          </div>
        )}
        {line && line > 0 && (
          <div className={flexRow}>
            <div
              className={css`
                flex-basis: 5em;
              `}
            >
              <FormattedMessage
                id="title_attackEventDetail_codeLine"
                children={(text) => <Fragment>{text}:</Fragment>}
              />
            </div>
            <div className={paddingLeft}>{line}</div>
          </div>
        )}
        {stackFrames && (
          <div>
            <FormattedMessage
              id="title_attackEventDetail_stack"
              children={(text) => <Fragment>{text}:</Fragment>}
            />
          </div>
        )}
      </div>
      {stackFrames && !!stackFrames.length && (
        <AttackEventStackFrames
          stackFrames={stackFrames}
          lastCustomFrame={lastCustomFrame}
        />
      )}
    </Fragment>
  )
}

const frameColorMap = {
  frameSink: css`
    color: ${theme.color.red50};
  `,
  frameCustom: css`
    color: ${theme.color.blue70};
  `
}

export function AttackEventStackFrames({
  stackFrames,
  lastCustomFrame
}: {
  readonly stackFrames: StackFrame[]
  readonly lastCustomFrame?: number
}) {
  return (
    <div
      className={
        css`
          margin-top: 1em;
        ` + ' well'
      }
    >
      <CollapsibleListManager
        max={lastCustomFrame ? lastCustomFrame + 1 : 3}
        items={stackFrames}
        children={({ visibleItems, isExpanded, onToggle, showToggle }) => {
          return (
            <Fragment>
              <TransitionUnorderedList
                className={css`
                  list-style-type: none;
                `}
              >
                {visibleItems.map((item, index) => (
                  <TransitionListItem
                    className={frameColorMap[item.type]}
                    children={item.description}
                    key={index}
                  />
                ))}
              </TransitionUnorderedList>

              {showToggle && (
                <CollapsibleListToggle
                  onToggle={onToggle}
                  isExpanded={isExpanded}
                />
              )}
            </Fragment>
          )
        }}
      />
    </div>
  )
}

export function AttackEventSuspiciousQueryElements({
  labelId,
  findings,
  possibleFindings,
  language
}: AttackEventSuspiciousQueryElementsProps) {
  return (
    <Fragment>
      <div
        className={css`
          margin-bottom: 0.5em;
          color: ${theme.color.neutral100};
        `}
      >
        <FormattedMessage id={labelId} values={{ count: findings.length }} />
      </div>
      {findings.map((finding) => {
        const ExamplesComponent = possibleFindings[finding]
        return (
          ExamplesComponent && (
            <ExamplesComponent key={finding} language={language} />
          )
        )
      })}
    </Fragment>
  )
}
